import React, { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import Circle from "@mui/icons-material/Circle";

function ChangeLog() {
  const [lightbox, setLightbox] = useState({ open: false, src: "", label: "" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const versionData = [
    {
      date: "26 August 2026",
      version: "28.02",
      logs: [
        {
          description: "On-Demand Salary Reveal",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "On-Demand Salary Reveal with Frosted Glass Effect",
          screenshotUrl: "/assets/images/changelog/v28-02/salary-reveal.png",
          subPoints: [
            "Salary figures are now hidden behind a frosted glass effect with a reveal button.",
            "Added progressive loading for salary breakdown sections.",
            "Improved individual row-level loading for salary details.",
          ],
        },
        {
          description: "Company Salary Cards Redesign",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "Redesigned Salary Breakdown Cards",
          screenshotUrl: "/assets/images/changelog/v28-02/salary-cards.png",
          subPoints: [
            "Redesigned salary breakdown cards with range progress bars for clearer comparison.",
            "Added loading placeholders for a smoother experience.",
          ],
        },
        {
          description: "Profile Edit & Re-Evaluation",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Editing your role, industry, experience, or location in the review form now automatically triggers a fresh salary analysis with updated data.",
            "Added role and skills information in Coaching Corner links for all industries.",
          ],
        },
        {
          description: "Data Quality Labels Update",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Updated match status labels to align with datapoints terminology for better clarity.",
          ],
        },
      ],
    },
    {
      date: "12 August 2026",
      version: "28.01",
      logs: [
        {
          description: "Skill & Role Disruption Insights",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          hasScreenshot: true,
          screenshots: [
            { url: "/assets/images/changelog/v28-01/disruption-insights-1.png", label: "Skill & Role Disruption Section" },
            { url: "/assets/images/changelog/v28-01/disruption-insights-2.png", label: "Skill & Role Disruption Section" },
          ],
          subPoints: [
            "Added new Skill & Role Disruption section with redundancy analysis, automation impact, salary disruption trends, and major industry disruption insights.",
            "Expandable sections for exploring industry-specific disruptions.",
          ],
        },
        {
          description: "Survival Strategy Section",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "Survival Strategy Section",
          screenshotUrl: "/assets/images/changelog/v28-01/survival-strategy.png",
          subPoints: [
            "Introduced a standalone Survival Strategy section with actionable up-skilling recommendations.",
            "Enhanced navigation icons with distinct thematic colors for each insight section.",
          ],
        },
      ],
    },
    {
      date: "31 July 2026",
      version: "28.00",
      logs: [
        {
          description: "Role Match Insights",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],          
          subPoints: [
            "Added Role Match progress bar with dynamic color gradients based on match percentage.",
            "Added automatic detection of user type (candidate vs recruiter).",
            "Added descriptive data quality labels (e.g., 'Focused Datapoints With Skills', 'Role Specific Datapoints').",
            "Renamed 'Match Quality' section to 'Market Salary Data Quality' for better clarity.",
          ],
        },
        {
          description: "Upload Form Validation",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added special character validation for the role field.",
            "Blocked combined roles using the 'and' keyword and allowed hyphens for accuracy.",
            "Added validation warnings to guide users before form submission.",
            "Fixed location field labels to allow proper input focus on click.",
          ],
        },
        {
          description: "Progress Bar Enhancements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],          
          subPoints: [
            "Replaced emoji icons with custom-designed step icons in the progress bar.",
            "Added cycling icons for a more engaging progress experience.",
            "Improved icon loading speed.",
          ],
        },
        {
          description: "Salary Display Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Rounded all salary values to the nearest thousand for a cleaner display.",
            "Simplified maximum salary calculation for more consistent results.",
          ],
        },
        {
          description: "Error Handling Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added user-friendly messages when the service is busy or temporarily unavailable.",
            "Improved upload error messages to show actual error details instead of generic messages.",
          ],
        },
        {
          description: "Faster Processing",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Skipped unnecessary processing steps when data is already available, reducing wait time.",
          ],
        },
        {
          description: "Result Page Updates",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added graceful handling when market data is temporarily unavailable.",
            "Enhanced data quality tags with detailed field values.",
            "Added detailed information in evaluation tags.",
            "Repositioned resume badge and added side theme icons on mobile.",
          ],
        },
        {
          description: "Navigation & Usability",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added 'Back to Upload' button with process cancellation during analysis.",
            "Added favicon for better browser tab identification.",
            "Updated result page to display actual generation date and time.",
          ],
        },
        {
          description: "Security Enhancements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added data encryption for request and response communication.",
          ],
        },
      ],
    },
    {
      date: "22 July 2026",
      version: "27.05",
      logs: [
        {
          description: "Skill Search Performance Improvement",
          apps: [
            { name: "WorthySalary", url: "https://worthysalary.trueselfy.com" },
            { name: "Career", url: "https://career.trueselfy.com" },
          ],
          subPoints: [
            "Optimized skill search to respond faster by reducing unnecessary search requests while typing.",
            "Improved search experience in Skills, Certifications, and other multi-select fields.",
            "Improved skill search speed across career relevance analysis.",
          ],
        },
        {
          description: "Productivity Tools Section",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Renamed 'AI Tools' section to 'Productivity Tools' for better clarity.",
            "Fixed flickering numbers in salary increase and ease of learning tables.",
          ],
        },
      ],
    },
    {
      date: "30 June 2026",
      version: "27.04",
      logs: [
        {
          description: "AI Tools Recommendations",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          hasScreenshot: true,
          screenshots: [
            { url: "/assets/images/changelog/v27-04/ai-tools-dashboard-1.png", label: "AI Tools Recommendation Dashboard" },
            { url: "/assets/images/changelog/v27-04/ai-tools-dashboard-2.png", label: "AI Tools Recommendation Dashboard" },
          ],
          subPoints: [
            "Added new AI Tools recommendations dashboard with impact, time saved, ROI, and priority metrics.",
            "Added 'Explore Tool' button to visit recommended tool websites directly.",
            "Optimized mobile layout for the AI Tools section.",
            "Section and bookmark are automatically hidden when recommendations are unavailable.",
          ],
        },
        {
          description: "Industry-Based Theme Switching",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],         
          subPoints: [
            "Added dynamic background themes based on detected industry (IT, Healthcare, Banking, Retail, BioTechnology).",
            "Theme updates in real-time when editing industry in the review form.",
            "Added industry theme icons for mobile progress view.",
          ],
        },
        {
          description: "Result Page Enhancements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added matching combo details in data sources section for transparency.",
            "Improved buttons, tags, and 'View More' styling across the result page.",
            "Updated Evaluation Feedback layout for better readability.",
          ],
        },
        {
          description: "Salary Display Fixes",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added fallback salary data when company-size breakdown has missing values.",
            "Hidden empty salary and market intelligence sections automatically.",
            "Filtered out zero-value skill premiums and hidden empty demand bars.",
          ],
        },
        {
          description: "Error Handling & Timestamps",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Improved error messages for clearer user guidance.",
            "Added result generation timestamp on the result page.",
            "Suppressed unnecessary error popups for background processes.",
          ],
        },
      ],
    },
    {
      date: "29 May 2026",
      version: "27.03",
      logs: [
        {
          description: "Faster CV Processing",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          subPoints: [
            "Improved CV Score and Top Skills processing to avoid timeouts on slower connections.",
            "Resume parsing, score calculation, and What If Analysis now use a faster processing method.",
            "Skills and form data load in parallel for quicker results.",
            "Added automatic retry with smart backoff when server is busy.",
            "Friendly timeout messages when processing takes longer than expected.",
            "Automatic fallback to primary skills if top skills processing times out.",
          ],
        },
        {
          description: "Previous Result Access",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added 'Previous Result' button to quickly access the last salary analysis.",
            "Previous results now open in a new tab using a unique shareable link.",
            "Fixed popup-blocker issue when opening previous results.",
          ],
        },
        {
          description: "Profile Review & Edit",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Users can now review and edit their extracted profile before final analysis.",
            "Added auto-submit countdown timer on the review form.",
            "Updated progress bar messages to reflect user-reviewed data.",
            "Fixed submit button getting disabled when the State field is empty.",
            "Fixed location label display issue on the review form.",
          ],
        },
        {
          description: "Market Intelligence & Salary Breakdown",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "Market Intelligence & Company Size Salary Breakdown",
          screenshotUrl: "/assets/images/changelog/v27-03/market-intelligence.png",
          subPoints: [
            "Added Market Intelligence section with dynamic currency support on the result page.",
            "Introduced company-size salary breakdown showing salary ranges by company scale.",
            "Revamped Market Intelligence UI to surface hidden salary insights.",
            "Added location-aware currency formatting for all salary figures.",
            "Fixed salary comparison to only compare when both results share the same currency.",
            "Improved salary comparison accuracy using country-wise converted values.",
          ],
        },
        {
          description: "User-Friendly Error Messages",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Introduced clear, descriptive error messages for all failures.",
            "Replaced technical error codes with easy-to-understand guidance and suggestions.",
            "Fixed duplicate error notification issue.",
          ],
        },
        {
          description: "Feedback & Display Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added feedback buttons on the result page to collect user opinions.",
            "Sorted evaluation feedback tags by category for better organization.",
            "Improved font sizing for salary breakdown readability.",
            "Removed text truncation in Skills and Evaluation Feedback sections for full readability.",
            "Fixed tooltip and popup overlapping issue when clicking on parameters.",
            "Removed obsolete salary sections and simplified parsing disclaimer.",
          ],
        },
        {
          description: "Label & Text Corrections",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Updated salary label to 'Maximum Potential Salary'.",
            "Fixed 'TechValue' text display issue on the result page.",
            "Fixed empty parsed data handling for edge-case resumes.",
          ],
        },
        {
          description: "Performance Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Reduced overall processing wait time by optimizing the analysis pipeline.",
            "Improved top-skills loading speed.",
            "Fixed login loader and Sign-In button loading state issues.",
          ],
        },
        {
          description: "Google Analytics Integration",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added Google Analytics tracking for production environment.",
          ],
        },
      ],
    },
    {
      date: "28 April 2026",
      version: "27.02",
      logs: [
        {
          description: "New Theme — Dhurandhar Movie Theme",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          hasScreenshot: true,
          screenshots: [
            { url: "/assets/images/changelog/v27-02/dhurandhar-home.png", label: "Dhurandhar Theme — Homepage" },
            { url: "/assets/images/changelog/v27-02/dhurandhar-upload.png", label: "Dhurandhar Theme — Upload Page" },
            { url: "/assets/images/changelog/v27-02/dhurandhar-result.png", label: "Dhurandhar Theme — Result Page" },
          ],
          subPoints: [
            "Added cinematic spy-thriller themed experience inspired by Dhurandhar.",
            "Crimson red and warm gold colour palette with dark backdrop.",
            "Animated gold dust particles, red embers, and rotating crosshair effects.",
            "Custom result page with gradient text and glass-style cards.",
            "20 spy-themed progress messages during resume upload.",
            "Full theme support across homepage, upload, and result pages.",
          ],
        },
        {
          description: "Evaluation Feedback UX Overhaul",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Enhanced evaluation feedback with themed dialogs and organized category ordering.",
            "Improved parameter name formatting for clearer display.",
          ],
        },
        {
          description: "Faster Processing",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Migrated all core processing to a faster scalable architecture.",
            "Added parallel processing for market data and salary analysis.",
            "Improved handling of failed uploads with automatic retries.",
          ],
        },
        {
          description: "Upload & Result Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Improved progress bar with user-friendly messages and optimized timing.",
            "Added seamless transition from upload progress to results page.",
            "Uploaded resume now opens in a new tab when clicked.",
            "Reordered result info sections and tightened salary section spacing.",
            "Improved page navigation speed.",
          ],
        },
        {
          description: "Experience & Salary Comparison",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Displayed the higher experience value when multiple data sources are available.",
            "Added career redirection with role information for non-tech industries.",
            "Improved experience calculation logic for more accurate results.",
          ],
        },
        {
          description: "Contact Us Page Redesign",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Redesigned Contact Us page with working email functionality.",
          ],
        },
        {
          description: "Authentication Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Enhanced sign-in flow and session handling.",
            "Updated OTP input to accept only numeric characters.",
          ],
        },
      ],
    },
    {
      date: "31 March 2026",
      version: "27.01",
      logs: [
        {
          description: "Evaluation Feedback System",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          hasScreenshot: true,
          screenshots: [
            { url: "/assets/images/changelog/v27-01/evaluation-feedback-1.png", label: "Evaluation Feedback with Color-Coded Categories" },
            { url: "/assets/images/changelog/v27-01/evaluation-feedback-2.png", label: "Evaluation Feedback with Color-Coded Categories" },
          ],
          subPoints: [
            "Introduced evaluation feedback with color-coded categories (Green, Amber, Red).",
            "Added evaluation feedback preview messages during resume processing.",
            "Added evaluation feedback boxes with tags, tooltips, and 'View More' on result page.",
            "Added count badge on each evaluation category header.",
            "Added evaluation progress messages during analysis.",
          ],
        },
        {
          description: "Animated Progress Experience",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],         
          subPoints: [
            "Added animated progress indicators with themed animations during file processing.",
            "Added dynamic detail messages based on extracted resume data.",
            "Enhanced progress tracking with smooth transitions.",
            "Standardized role labels and progress messaging for clarity.",
            "Updated upload section visuals with new images.",
          ],
        },
        {
          description: "Skills & Role Matching",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added top-skills display with color-coded skill indicators.",
            "Added candidate skills extraction from resume.",
            "Improved matching of similar job titles for consistent results.",
            "Added 'Other Roles' card showing role synonyms as expandable tags.",
            "Role synonyms and overall score now displayed in results.",
            "Role synonyms now generated based on the user's specific industry.",
            "Candidate role and industry now displayed on the result page.",
          ],
        },
        {
          description: "Salary Calculation & Display",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Updated salary calculation formula for improved accuracy.",
            "Introduced currency formatting based on user's location.",
            "Added profile match percentage display on result page.",
            "Added personalized salary recommendations for tech industry.",
            "Fixed salary sign display issue.",
            "Set default location to 'Other, India' for users without a detected location.",
          ],
        },
        {
          description: "Resume Parsing Improvements",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Improved resume parsing for more accurate job title and role extraction.",
            "Enhanced industry detection accuracy.",
            "Added experience calculation from work history.",
            "Added validation to block unsupported industries before processing.",
          ],
        },
        {
          description: "Performance & Reliability",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Resolved processing timeout issues for all major operations.",
            "Improved reliability with automatic retries on failures.",
            "Fixed slow response time during resume evaluation.",
          ],
        },
        {
          description: "Authentication & Navigation",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added logout functionality with confirmation dialog.",
            "Improved sign-in redirect handling across pages.",
            "Added animations on result and upload pages for a richer visual experience.",
          ],
        },
        {
          description: "Background Salary Comparison",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Integrated CVScore salary comparison for tech industry users.",
          ],
        },
      ],
    },
    {
      date: "19 March 2026",
      version: "27.00",
      logs: [
        {
          description: "New Animated Logo Loader",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Introduced a smooth animated logo loader across all loading screens for a modern look.",
            "Loading experience is now responsive and consistent on both mobile and desktop.",
          ],
        },
        {
          description: "Result Page Design Refresh",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Improved the overall design and layout of the Result page.",
            "Unified the design for Current Result and Previous Result pages for a consistent experience.",
          ],
        },
        {
          description: "Free Coaching Corner",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          subPoints: [
            "Added 'Free Coaching Corner' button on homepage and upload page.",
            "Easy access to free coaching resources with click tracking.",
          ],
        },
        {
          description: "Interview Questions Improvements",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Improved loading behavior for interview questions with better error handling and retry logic.",
          ],
        },
        {
          description: "Fixed currency display issue for accurate salary representation.",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
        },
      ],
    },
    {
      date: "27 February 2026",
      version: "26.05",
      logs: [
        {
          description: "MaxSalary — Application Launch",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          hasScreenshot: true,
          screenshots: [
            { url: "/assets/images/changelog/v26-05/maxsalary-upload.png", label: "MaxSalary — Upload Page" },
            { url: "/assets/images/changelog/v26-05/maxsalary-result.png", label: "MaxSalary — Result Page" },
          ],
          subPoints: [
            "Launched a new salary analysis platform.",
            "Users can upload their resume (PDF) and receive a comprehensive salary analysis.",
          ],
        },
        {
          description: "Resume Upload & Parsing",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Added Resume Upload page with file preview and file validations.",
            "Integrated AI-powered resume parsing to extract work experience, skills, role, and industry.",
          ],
        },
        {
          description: "Salary Calculation Engine",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Built salary analysis engine based on role and industry.",
            "Added resume evaluation for comprehensive resume assessment.",
            "Implemented market intelligence for real-time salary benchmarking.",
            "Added final salary calculation and result generation.",
            "Added top 5 skills identification from resume.",
          ],
        },
        {
          description: "Result Page",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Designed result page displaying salary analysis, top skills, and evaluation details.",
            "Added automatic redirection to result page after processing completes.",
          ],
        },
        {
          description: "User Authentication",
          apps: [{ name: "MaxSalary", url: "https://maxsalary.trueselfy.com" }],
          subPoints: [
            "Implemented sign-in flow with email and OTP verification.",
          ],
        },
        {
          description: "Dynamic Footer Links",
          apps: [
            { name: "WorthySalary", url: "https://worthysalary.trueselfy.com" },
            { name: "Career", url: "https://career.trueselfy.com" },
            { name: "CVScore", url: "https://cvscore.trueselfy.com" },
            { name: "ViewResume", url: "https://viewresume.trueselfy.com" },
          ],
          subPoints: [
            "Footer links are now dynamically loaded from configuration across all applications.",
            "Added fallback support — default footer links display automatically if server is unavailable.",
            "Internal links now navigate within the app instead of opening a new tab.",
          ],
        },
        {
          description: "Authentication & Session Management",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Improved login and session handling for a smoother user experience.",
            "Enhanced sign-in security and error handling.",
            "Improved page load speed by caching device and location data.",
          ],
        },
        {
          description: "Skill Selection & Loading Improvements",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Improved auto-selection of top skills for quicker results.",
            "Enhanced loading indicators for a better form experience.",
          ],
        },
        {
          description: "Educational Qualification & Work Experience Design Update",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Refreshed the design of Educational Qualification and Work Experience sections for a cleaner look.",
          ],
        },
        {
          description: "Incognito Mode Improvements",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Improved Incognito Mode styling and validation in the Work Experience section.",
            "Enhanced visual distinction between regular and incognito user views.",
          ],
        },
        {
          description: "URL & Environment Updates",
          apps: [{ name: "ViewResume", url: "https://viewresume.trueselfy.com" }],
          subPoints: [
            "Updated TechWorth and application URLs across development and testing environments to use custom branded domains.",
            "Enhanced project documentation with improved overview, features list, and deployment instructions.",
          ],
        },
        {
          description: "Performance & Stability Improvements",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Optimized analytics tracking to run only in production for better performance.",
            "Improved work experience data filtering on Result and Previous Result pages.",
            "Enhanced year selector for smoother date selection in Work Experience.",
          ],
        },
      ],
    },
    {
      date: "19 February 2026",
      version: "26.04",
      logs: [
        {
          description: "Career Path Visualization",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Added a visual career path tree showing potential higher roles and growth opportunities.",
          ],
        },
        {
          description: "Interview Questions by Category",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "Categorized Interview Questions",
          screenshotUrl: "/assets/images/changelog/v26-04/interview-questions.png",
          subPoints: [
            "Interview questions are now organized by category with search and pagination for easier browsing.",
          ],
        },
        {
          description: "Career Guidance Corner",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Renamed 'View More Skill Detail' to 'Career Guidance Corner' for better clarity.",
            "Improved link styling with hover effects and responsive text sizing.",
          ],
        },
        {
          description: "Incognito Mode for Work Experience",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Added Incognito Mode support in the Work Experience section.",
            "Dynamic background styling based on user's incognito status.",
          ],
        },
        {
          description: "Result Page Text Display Improvements",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Fixed long text overflow issues on Result and Previous Result pages.",
            "Long dropdown values now show a tooltip on hover for full visibility.",
            "Improved handling of Hobbies & Interests display across different data formats.",
          ],
        },
        {
          description: "Skills Redirection Enhancement",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Skills can now be redirected from multiple partner applications, not limited to a single source.",
          ],
        },
        {
          description: "URL & Configuration Updates",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Updated base URLs for consistent cross-environment configuration.",
          ],
        },
      ],
    },
    {
      date: "13 February 2026",
      version: "26.03",
      logs: [
        {
          description: "What If Analysis Feature",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          hasScreenshot: true,
          screenshotLabel: "What If Analysis on Result Page",
          screenshotUrl: "/assets/images/changelog/v26-03/what-if-analysis.png",
          subPoints: [
            "New 'What If Analysis' section on the result page to explore salary changes based on different factors.",
            "Adjust Type of Company, Work Mode, Employment Type, and Contract Type to see projected salary impact.",
            "Type of Company displayed as easy-to-use stepper instead of slider.",
            "Dynamic Work Mode options with visual icons for each mode.",
            "Employment Type selection with modern switch button design.",
            "Contract Type selection (Full Time / Part Time) with salary impact.",
            "Animated loader while salary recalculates.",
            "Reset button to restore default values.",
            "Available across all themes.",
          ],
        },
        {
          description: "Work Experience Section Revamp",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Redesigned the Work Experience form with searchable dropdowns for faster date selection.",
            "Added 'Currently Working' option with smart auto-detection.",
            "Improved form validation to guide users through required fields.",
            "Enhanced responsive design for better mobile experience.",
          ],
        },
        {
          description: "New Input Fields: Work Activities, Hobbies & Interests, Achievements",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Added new editable multi-select fields for Work Activities, Hobbies & Interests, and Achievements.",
            "Users can select from predefined options or add custom entries.",
            "Chip-based display with easy edit and delete functionality.",
            "Dedicated Work Activities selector with editable autofill support.",
          ],
        },
        {
          description: "Date Picker Improvements",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Fixed issues where invalid dates could be selected.",
            "Users can no longer select future dates for Work Experience.",
            "Expanded year selection range for better coverage.",
          ],
        },
        {
          description: "Salary Display Updates",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          subPoints: [
            "Now shows Market Salary instead of Average Salary for more accurate representation.",
            "Annual Salary value displayed in larger, more prominent style.",
          ],
        },
        {
          description: "Result Page UI Enhancements",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
          subPoints: [
            "Added attention-grabbing jittering animation effect to salary result highlights.",
          ],
        },
        {
          description: "Updated application URLs for TechWorth and TechRelevance services.",
          apps: [{ name: "CVScore", url: "https://cvscore.trueselfy.com" }],
        },
      ],
    },
    {
      date: "30 January 2026",
      version: "26.02",
      logs: [
        {
          description: "Branding & URL Updates",
          apps: [
            { name: "Career", url: "https://career.trueselfy.com" },
            { name: "WorthySalary", url: "https://worthysalary.trueselfy.com" },
          ],
          subPoints: [
            "Updated TechRelevance URL to career.trueselfy.com for a more user-friendly domain.",
            "Updated TechWorth references to worthysalary.trueselfy.com across partner apps.",
          ],
        },
        {
          description: "Skill Detail & Career Guidance Link",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Added a new 'View More Skill Detail' link for detailed skill insights and career guidance.",
            "Updated Career Boost links across the app to point to the new career.trueselfy.com domain.",
          ],
        },
        {
          description: "UI/UX Improvements",
          apps: [{ name: "Career", url: "https://career.trueselfy.com" }],
          subPoints: [
            "Improved dropdown visibility and layering in the relevance check form.",
            "Enhanced accordion behavior in Roles and Responsibilities section.",
            "Responsive icon sizing for Latest Technologies and Get Insights sections.",
          ],
        },
      ],
    },
    {
      date: "05 January 2026",
      version: "26.01",
      logs: [
        {
          description: "App Rebranding to WorthySalary",
          apps: [
            { name: "WorthySalary", url: "https://worthysalary.trueselfy.com" },
            { name: "ViewResume", url: "https://viewresume.trueselfy.com" },
          ],
          subPoints: [
            "Updated the application domain from TechWorth to WorthySalary across the entire app.",
            "All internal and external links now reflect the new WorthySalary branding.",
          ],
        },
        {
          description: "Skill Icons Enhancement",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
          subPoints: [
            "Improved skill icon matching and display for better visual accuracy.",
          ],
        },
        {
          description: "Standardized internal file structure for improved app stability and maintainability.",
          apps: [{ name: "WorthySalary", url: "https://worthysalary.trueselfy.com" }],
        },
      ],
    },
    {
      date: "31 December 2025",
      version: "26.00",
      logs: [
        {
          description: "Asset Updates",
          subPoints: [
            "Added new Skills and AI Tools icons in assets.",
            "Updated icon database for improved skill representation.",
          ],
        },
        {
          description: "SkillTree Enhancements",
          subPoints: [
            "Multi-LLM support with AI model selection (OpenAI/Gemini).",
            "N-level skill tree expansion with improved state management.",
            "Mobile detection and exclusive node expansion.",
            "Caching and loading states for better performance.",
          ],
        },
        {
          description: "Added new Skills and AI Tools icons in assets and database.",
        },
        {
          description: "Skill Icons Enhancement",
          subPoints: [
            "Added new icons for skills to improve visual representation.",
          ],
        },
        {
          description: "Security and Environment Updates",
          subPoints: [
            "Removed MongoDB URI from environment variables for enhanced security.",
            "Updated environment variable handling and configuration.",
          ],
        },
      ],
    },
    {
      date: "26 December 2025",
      version: "25.05",
      logs: [
        {
          description: "Salary Growth Projection Feature",
          subPoints: [
            "Added Salary Growth Projection Chart using ApexCharts.",
            "Integrated Harness feature flag for chart visibility.",
            "Enhanced FileUpload and ResultPage components for line chart data handling.",
          ],
        },
      ],
    },
    {
      date: "08 December 2025",
      version: "25.04",
      logs: [
        {
          description: "SkillTree & Content Renderer Updates",
          subPoints: [
            "Enhanced SkillTree for improved user interaction and data handling.",
            "Updated terminology from 'Learn & Earn Recommendations' to 'Latest Technologies'.",
            "Added skeleton loader and improved loading visuals for Subskills.",
          ],
        },
        {
          description: "Refactored HomePage and BasicRelevanceCheckDark components for improved layout.",
        },
      ],
    },
    {
      date: "28 November 2025",
      version: "25.03",
      logs: [
        {
          description: "Kite Festival Theme",
          subPoints: [
            "Introduced Kite Festival theme with varied animation patterns.",
            "Enhanced visibility adjustments for kite animations.",
            "Refactored FileUpload component and theme content.",
          ],
        },
        {
          description: "All Elements Page",
          subPoints: [
            "Display 'Missing Info' slide first in AllElements component.",
            "Added buttons for profile completion and factor adjustment.",
          ],
        },
        {
          description: "Infrastructure Updates",
          subPoints: ["Updated amplify.yml to use Node.js version 20."],
        },
        {
          description: "Result Page Design Improvements",
          subPoints: [
            "Updated icon container and typography for responsive design.",
            "Fixed icon placement and proceed button positioning.",
          ],
        },
        {
          description: "Incognito Mode & Resume Upload Enhancements",
          subPoints: [
            "Updated component styles for improved accessibility and aesthetics.",
          ],
        },
        {
          description: "Login Optimization",
          subPoints: [
            "Optimized AuthContext and ProtectedRoute for better performance.",
            "Updated country validation logic.",
          ],
        },
        {
          description: "SEO Improvements",
          subPoints: [
            "Commented out Joyride tour on homepage for SEO purposes.",
          ],
        },
      ],
    },
    {
      date: "20 November 2025",
      version: "25.02",
      logs: [
        {
          description: "Republic Day Theme",
          subPoints: [
            "Added Republic Day theme with wave animations and flag display.",
            "Updated theme surface colors for improved visibility.",
            "Enhanced visual consistency across result page components.",
          ],
        },
        {
          description: "New Year 2026 Theme",
          subPoints: [
            "Introduced New Year 2026 theme with festive animations.",
            "Added theme-specific assets and content.",
          ],
        },
        {
          description: "Top Skills Interview Questions",
          subPoints: [
            "Razorpay payment integration for premium interview questions.",
            "Monthly subscription plan for interview questions.",
            "Enhanced loading state with detailed skeletons.",
          ],
        },
        {
          description: "Google AdSense Integration",
          subPoints: [
            "Integrated Google AdSense and implemented ad tracking.",
          ],
        },
        {
          description: "Session storage for relevance results.",
        },
      ],
    },
    {
      date: "06 November 2025",
      version: "25.01",
      logs: [
        {
          description: "Christmas Theme Enhancements",
          subPoints: [
            "Added mobile-specific background image for Christmas theme.",
            "Enhanced Christmas theme styles and effects.",
            "Updated theme assets including Christmas bells.",
          ],
        },
        {
          description: "Dependency Updates",
          subPoints: [
            "Removed react-web-share dependency.",
            "Added TypeScript 5.9.3 support.",
          ],
        },
      ],
    },
    {
      date: "17 October 2025",
      version: "25.00",
      logs: [
        {
          description: "TechWorth URL Updates",
          subPoints: [
            "Enhanced TechWorth links with personalized email query parameters.",
            "Improved URL construction across multiple components.",
          ],
        },
        {
          description: "All Elements Page & Routing",
          subPoints: [
            "Added all-elements page with redirect handling.",
            "Implemented saveTargetPath utility for candidate profile routing.",
            "Enhanced ProtectedRoute for URL handling and session storage.",
          ],
        },
      ],
    },
    {
      date: "16 October 2025",
      version: "24.05",
      logs: [
        {
          description: "Christmas Theme Launch",
          subPoints: [
            "Added Christmas theme with snow animations and festive result pages.",
            "Now supports 5 themes: Simple, Cricket, Earth, Diwali, and Christmas.",
          ],
        },
        {
          description: "Salary Display Updates",
          subPoints: [
            "Added disclaimer about resume parsing errors.",
            "Updated salary button labels to 'Get Better - Personalized Salary'.",
            "Changed tech value labels to 'Maximum Potential Salary'.",
          ],
        },
        {
          description: "CV Score Integration",
          subPoints: [
            "Enhanced ProtectedRoute and stepper form to handle URL parameters for cvscore and useremail.",
          ],
        },
        {
          description: "Resume Features",
          subPoints: [
            "Added resume viewing functionality to SingleStepForm component.",
            "Updated button text in ResumeUpload component based on active step.",
          ],
        },
      ],
    },
    {
      date: "13 October 2025",
      version: "24.04",
      logs: [
        {
          description: "CVScore Integration",
          subPoints: [
            "Auto-click 'Check Relevance' button after skills populated from CVScore redirect.",
            "Safeguards for simultaneous submissions.",
            "Enhanced skill validation handling.",
          ],
        },
        {
          description: "Improved data validation in HigherRolesTree and RolesAndResponsibilities components.",
        },
      ],
    },
    {
      date: "11 October 2025",
      version: "24.03",
      logs: [
        {
          description: "SEO Enhancements",
          subPoints: [
            "Added Google and Bing Search Console verification codes.",
            "Enhanced metadata for improved SEO performance.",
            "Integrated Google Analytics tracking.",
          ],
        },
        {
          description: "UI/UX Improvements",
          subPoints: [
            "Updated homepage messaging and text hierarchy.",
            "Fixed share button to prevent opening multiple windows.",
            "Added Resume Badge feature.",
          ],
        },
      ],
    },
    {
      date: "29 September 2025",
      version: "24.02",
      logs: [
        {
          description: "Security Enhancements",
          subPoints: [
            "Implemented data encryption and decryption for resume upload and form submission.",
            "Expanded object cleanup function for comprehensive data handling.",
          ],
        },
      ],
    },
    {
      date: "27 September 2025",
      version: "24.01",
      logs: [
        {
          description: "Cricket Theme Enhancements",
          subPoints: [
            "Enhanced CricketAnimation with improved visuals and gameplay mechanics.",
            "Refined Cricket Scoreboard with responsive design.",
          ],
        },
      ],
    },
    {
      date: "24 September 2025",
      version: "24.00",
      logs: [
        {
          description: "Theme Result Pages",
          subPoints: [
            "Added Earth and Diwali themed result pages.",
            "Integrated Redux for state management in FileUpload and ResultPage.",
            "Enhanced theming and animations across the application.",
          ],
        },
      ],
    },
    {
      date: "22 September 2025",
      version: "23.05",
      logs: [
        {
          description: "Enhanced skill handling and URL management for CVScore skill querystring.",
        },
      ],
    },
    {
      date: "19 September 2025",
      version: "23.04",
      logs: [
        {
          description: "Diwali Theme",
          subPoints: [
            "Added Fireworks animation for Diwali theme.",
            "Enhanced FireworksBackground with improved Diwali-themed effects.",
            "Updated homepage components for better user experience.",
          ],
        },
      ],
    },
    {
      date: "17 September 2025",
      version: "23.03",
      logs: [
        {
          description: "Cricket Scoreboard Feature",
          subPoints: [
            "Implemented Cricket Scoreboard component with responsive design.",
            "Added skill icons display in Cricket theme.",
            "Enhanced Coaching Corner URL generation.",
          ],
        },
      ],
    },
    {
      date: "15 September 2025",
      version: "23.02",
      logs: [
        {
          description: "Theme Enhancements",
          subPoints: [
            "Updated Earth and Diwali theme color palettes.",
            "Improved CSS variables for enhanced visual consistency.",
            "Added smoother animation transitions.",
          ],
        },
      ],
    },
    {
      date: "12 September 2025",
      version: "23.01",
      logs: [
        {
          description: "Skills Icons & Theme Management",
          subPoints: [
            "Added skills icons in assets.",
            "Refactored theme management system.",
            "Enhanced content management across components.",
          ],
        },
      ],
    },
    {
      date: "10 September 2025",
      version: "23.00",
      logs: [
        {
          description: "File Validation & User Feedback",
          subPoints: [
            "Added comprehensive file validation for uploads.",
            "Enhanced user-friendly error messages.",
            "Improved FileUpload component with retry logic.",
          ],
        },
      ],
    },
    {
      date: "05 September 2025",
      version: "22.05",
      logs: [
        {
          description: "Project Redesign",
          subPoints: [
            "Migrated to Next.js framework.",
            "Added resume upload feature with validation.",
            "Introduced theme-based architecture.",
            "Added Upload Loader component with progress indicators.",
          ],
        },
      ],
    },
    {
      date: "21 August 2025",
      version: "22.04",
      logs: [
        {
          description: "JD Ranker & Admin Features",
          subPoints: [
            "Added changes for JD Ranker improvements.",
            "Implemented superAdmin state management and UI updates.",
            "Enhanced LoginLoading and CollapsibleTableNew components.",
          ],
        },
      ],
    },
    {
      date: "19 August 2025",
      version: "22.03",
      logs: [
        {
          description: "Feature Flag Improvements",
          subPoints: [
            "Added async prop to FFContextProvider for improved feature flag handling.",
          ],
        },
      ],
    },
    {
      date: "18 August 2025",
      version: "22.02",
      logs: [
        {
          description: "New SEO-Optimized HomePage",
          subPoints: [
            "Added CareerInsightsSection with video background.",
            "Added HowItWorksSection and RelevanceTimelineSection.",
            "Added WhyUseThisToolSection and WhoIsThisForSection.",
            "Added FAQ and InterviewQuestionSection.",
            "Integrated SignInModal for user authentication.",
          ],
        },
        {
          description: "Redux Integration",
          subPoints: [
            "Refactored App component to integrate Redux and enhance routing.",
          ],
        },
      ],
    },
    {
      date: "06 August 2025",
      version: "22.01",
      logs: [
        {
          description: "UI Improvements",
          subPoints: [
            "Updated ResumeUploadRecruiter selected resume line height and font size for improved readability.",
          ],
        },
      ],
    },
    {
      date: "04 August 2025",
      version: "22.00",
      logs: [
        {
          description: "BookmarkNavigation & UI Enhancements",
          subPoints: [
            "Added BookmarkNavigation for section navigation.",
            "Added InsightsAnimation component for animated text during processing.",
            "Auto-click Ask AI button feature.",
            "Tour Guide for Courses section.",
          ],
        },
        {
          description: "Courses Section",
          subPoints: [
            "Added courses section with YouTube and Google search.",
            "Gemini and ChatGPT buttons with prompt dialog.",
          ],
        },
      ],
    },
    {
      date: "31 July 2025",
      version: "21.05",
      logs: [
        {
          description: "Higher Roles Visualization",
          subPoints: [
            "Added higher roles tree visualization.",
            "Tour guide for all GetInsights sections.",
          ],
        },
        {
          description: "Widget Page",
          subPoints: [
            "Added TechRelevance carousel widget.",
            "Code snippets showcase page.",
          ],
        },
        {
          description: "Refactored dropdowns to use Autocomplete components.",
        },
      ],
    },
    {
      date: "29 July 2025",
      version: "21.04",
      logs: [
        {
          description: "TechWorth & Public Profile",
          subPoints: [
            "Added target blank and cursor pointer for TechWorth references.",
            "Show SmartSalary into Recruiter Public Profile Page.",
            "Added llms.txt file.",
          ],
        },
      ],
    },
    {
      date: "24 July 2025",
      version: "21.03",
      logs: [
        {
          description: "AI Integration",
          subPoints: [
            "Added llms.txt file for AI/LLM integrations.",
          ],
        },
      ],
    },
    {
      date: "23 July 2025",
      version: "21.02",
      logs: [
        {
          description: "Resume Ranker Enhancements",
          subPoints: [
            "Added Action column with UUID visibility and Profile icon button.",
            "Display public profile of selected resume in ResumeRanker.",
          ],
        },
      ],
    },
    {
      date: "18 July 2025",
      version: "21.01",
      logs: [
        {
          description: "Salary Range by Location",
          subPoints: [
            "Added salary range by state and city.",
            "Enhanced location selection with user location detection.",
            "Income by experience carousel section.",
          ],
        },
        {
          description: "JobSuggestionComponent added for job recommendations.",
        },
        {
          description: "TechWorth redirect ads card below salary range section.",
        },
      ],
    },
    {
      date: "10 July 2025",
      version: "21.00",
      logs: [
        {
          description: "Resume Ranker Updates",
          subPoints: [
            "Updated salary conversion logic with convertedIndianSalary.",
            "Enhanced budget display with layout spacing and currency notes.",
            "Added SmartSalary column with highlightMatch function.",
          ],
        },
      ],
    },
    {
      date: "30 June 2025",
      version: "20.05",
      logs: [
        {
          description: "Resume Ranker & History Enhancements",
          subPoints: [
            "Added budget low and high fields to edit form with history prefill.",
            "Enhanced highlightText function for multiple search terms.",
            "Custom TablePagination component for better reusability.",
          ],
        },
        {
          description: "UI/UX Updates",
          subPoints: [
            "Mobile responsiveness for CollapsibleTableNew.",
            "Added tooltips to InfoRow components.",
            "Fit category and percentage display with status chips.",
          ],
        },
      ],
    },
    {
      date: "24 June 2025",
      version: "20.04",
      logs: [
        {
          description: "Roles and Responsibilities Section",
          subPoints: [
            "Suggested Future Roles with API integration.",
            "Other Roles with Responsibilities section.",
            "Higher Roles with Promotions section.",
            "Skeleton loaders for all role sections.",
          ],
        },
        {
          description: "Conditional robots.txt generation based on environment.",
        },
        {
          description: "SEO & Deployment Updates",
          subPoints: [
            "Added robots.txt generation logic for dev and test environments.",
          ],
        },
      ],
    },
    {
      date: "19 June 2025",
      version: "20.03",
      logs: [
        {
          description: "JD Ranker & Table Improvements",
          subPoints: [
            "Changed default operator for experience field from '=' to '>='.",
            "Added Fit % and fit category fields to child rows.",
            "Replaced table with CollapsibleTableNew component in JD ranker.",
          ],
        },
        {
          description: "Certification Features",
          subPoints: [
            "Added expand/collapse functionality for certifications in Result table.",
            "Certification display on chip and in detail view.",
          ],
        },
      ],
    },
    {
      date: "17 June 2025",
      version: "20.02",
      logs: [
        {
          description: "Skills & Synonyms",
          subPoints: [
            "Added skills synonyms API recall functionality.",
            "User can update, remove, or add skills to recall topskillsvalidity API.",
            "Role synonyms filtering improvements.",
          ],
        },
        {
          description: "Table Enhancements",
          subPoints: [
            "Added primarySkill dynamic data to Chip.",
            "Updated company & location placement across columns.",
            "Added collapsible prop to tables.",
          ],
        },
      ],
    },
    {
      date: "16 June 2025",
      version: "20.01",
      logs: [
        {
          description: "Global Table Component",
          subPoints: [
            "Refactored AdvancedDataSearch to use Global Table component.",
            "Added new SVG icons matching Figma design.",
            "Display group length in CollapsibleTableNew when expanded.",
          ],
        },
        {
          description: "Mobile UI Updates",
          subPoints: [
            "Added new icons and styles in DrawerAppBar for mobile.",
            "Enhanced InfoRow component for text wrapping and responsiveness.",
          ],
        },
      ],
    },
    {
      date: "14 June 2025",
      version: "20.00",
      logs: [
        {
          description: "Resume History & Search",
          subPoints: [
            "Enhanced CollapsibleTableNew with pagination and styling.",
            "Highlight search query in row names.",
            "Implemented ChildRowsLayout for nested rows.",
          ],
        },
        {
          description: "Skill Highlighting",
          subPoints: [
            "JD skill name matching highlights in results.",
            "Added CollapsibleTableMobile for responsive display.",
          ],
        },
      ],
    },
    {
      date: "11 June 2025",
      version: "19.05",
      logs: [
        {
          description: "Search Functionality",
          subPoints: [
            "Added search functionality with highlight feature in RecruiterResumeHistory.",
            "Debounced input with API integration.",
            "Text highlighting for matching results.",
          ],
        },
        {
          description: "Data Presentation",
          subPoints: [
            "Refactored CollapsibleTable with InfoRow component.",
            "Added loading state to UserCard component.",
          ],
        },
      ],
    },
    {
      date: "10 June 2025",
      version: "19.04",
      logs: [
        {
          description: "Advanced Search Updates",
          subPoints: [
            "Added utility functions for field operators and default values.",
            "Role synonyms display in RecruiterResumeProfile.",
            "Improved error handling with specific errors per resume.",
          ],
        },
      ],
    },
    {
      date: "09 June 2025",
      version: "19.03",
      logs: [
        {
          description: "API & Navigation Improvements",
          subPoints: [
            "Added retry logic for API calls in ResumeUploadRecruiter and JdwithDataSearch.",
            "Refactored links to use onClick navigation instead of target='_blank'.",
            "CollapsibleTableMobile pagination with loading state.",
          ],
        },
      ],
    },
    {
      date: "06 June 2025",
      version: "19.02",
      logs: [
        {
          description: "Skill Icon & Validation",
          subPoints: [
            "Introduced getIconBySkillPathName for skill icons using logoPath.",
            "Enhanced AdvancedDataSearch with filter validation.",
            "Added formatExperience utility function.",
          ],
        },
        {
          description: "UI Enhancements",
          subPoints: [
            "Row component hover effects and group highlighting.",
            "CollapsibleTable transparent background styling.",
          ],
        },
      ],
    },
    {
      date: "04 June 2025",
      version: "19.01",
      logs: [
        {
          description: "Resume Ranker Features",
          subPoints: [
            "Added encryption/decryption functions for JD and resume skill validity.",
            "Implemented average experience computation from min/max values.",
            "Added role synonyms, skills synonyms box, and experience level display.",
          ],
        },
        {
          description: "Error Handling",
          subPoints: ["Error handling for 502 and 504 status codes."],
        },
      ],
    },
    {
      date: "03 June 2025",
      version: "19.00",
      logs: [
        {
          description: "UI/UX Updates",
          subPoints: [
            "Enhanced date handling with formatted date and time display.",
            "Added beautifyLabel utility for Autocomplete options.",
            "Skill validity check in PendingResumes with encryption.",
          ],
        },
      ],
    },
    {
      date: "02 June 2025",
      version: "18.05",
      logs: [
        {
          description: "Resume Ranker Improvements",
          subPoints: [
            "File upload retry mechanism with improved error handling.",
            "Enhanced error messages for missing JD file, JD text, and resumes.",
            "Reset criteria form on file type changes.",
          ],
        },
      ],
    },
    {
      date: "30 May 2025",
      version: "18.04",
      logs: [
        {
          description: "Resume Ranker Flow",
          subPoints: [
            "Renamed to 'Resume Ranker (File Based)'.",
            "Added processing status icons and mobile responsiveness.",
            "Network handling with online/offline detection.",
          ],
        },
      ],
    },
    {
      date: "29 May 2025",
      version: "18.03",
      logs: [
        {
          description: "Job Description Features",
          subPoints: [
            "Support for text input in job descriptions.",
            "Improved error handling during file uploads.",
            "Added NetworkError and NetworkWatcher components.",
          ],
        },
      ],
    },
    {
      date: "28 May 2025",
      version: "18.02",
      logs: [
        {
          description: "Resume Ranker Enhancements",
          subPoints: [
            "Experience transformation logic and criteria management.",
            "Conditional rendering for job description content.",
            "Updated email validation message with allowed domains.",
          ],
        },
      ],
    },
    {
      date: "26 May 2025",
      version: "18.01",
      logs: [
        {
          description: "Processing Features",
          subPoints: [
            "Implemented cancel processing functionality.",
            "Clear error state on file removal.",
            "Disable buttons during loading state.",
          ],
        },
      ],
    },
    {
      date: "23 May 2025",
      version: "18.00",
      logs: [
        {
          description: "Resume Ranker History",
          subPoints: [
            "Added sidebar for Resume Ranker history with fetch and display.",
            "Click history record to prefill JD text and show results.",
            "Recruiter quota fetching and UI improvements.",
          ],
        },
      ],
    },
    {
      date: "22 May 2025",
      version: "17.05",
      logs: [
        {
          description: "Pending Resumes",
          subPoints: [
            "Multi-column sorting in PendingResumes component.",
            "Delete functionality with confirmation dialog.",
            "Renamed 'Advanced JD Search' to 'JD Ranker'.",
          ],
        },
      ],
    },
    {
      date: "21 May 2025",
      version: "17.04",
      logs: [
        {
          description: "Recruiter Features",
          subPoints: [
            "Added recruiter quota state and fetch on mount.",
            "JD Ranker design and sidebar updates.",
            "Sorting on file name in pending resumes.",
          ],
        },
      ],
    },
    {
      date: "20 May 2025",
      version: "17.03",
      logs: [
        {
          description: "JD Search History",
          subPoints: [
            "Added sidebar for JD ranking history.",
            "History record click prefills filter.",
            "Fixed infinite loop in data fetching.",
          ],
        },
      ],
    },
    {
      date: "19 May 2025",
      version: "17.02",
      logs: [
        {
          description: "Pending Resumes Table",
          subPoints: [
            "Show uploaded and unprocessed resumes on table.",
            "Added pagination and converted date/time column.",
          ],
        },
      ],
    },
    {
      date: "16 May 2025",
      version: "17.01",
      logs: [
        {
          description: "API & Validation",
          subPoints: [
            "Retry logic with exponential backoff for API calls.",
            "Trim whitespace from filter condition values.",
            "Hotfix for blacklist domains.",
          ],
        },
        {
          description: "Fit Category",
          subPoints: [
            "FitCategoryCell component for color-coded fit category.",
            "Support for single, multiple files, and zip file upload.",
          ],
        },
      ],
    },
    {
      date: "15 May 2025",
      version: "17.00",
      logs: [
        {
          description: "Resume Ranker Visual",
          subPoints: [
            "Fit Category column with colors (EXACT FIT, PARTIAL FIT, LESS OPTIMUM FIT, UNFIT).",
            "Multiple file selection with PDF files in payload.",
          ],
        },
      ],
    },
    {
      date: "14 May 2025",
      version: "16.05",
      logs: [
        {
          description: "Resume Ranker Features",
          subPoints: [
            "Fit % shown in descending order by default.",
            "Prefilled text for Job Description.",
            "Experience field accepts integer years only.",
          ],
        },
        {
          description: "Result Page Enhancements",
          subPoints: [
            "Refactored TechResultV5 component to use previousResultUserData for work experience, projects, achievements, education, languages, and hobbies.",
            "Improved data consistency and rendering logic for better performance.",
          ],
        },
        {
          description: "Resume Ranker Improvements",
          subPoints: [
            "Fixed work activities issues.",
            "Removed repeated components from result page.",
          ],
        },
      ],
    },
    {
      date: "13 May 2025",
      version: "16.04",
      logs: [
        {
          description: "JD Resume Ranker",
          subPoints: [
            "Combined JD Resume Ranker with Advanced Data Search.",
            "Added menu options for Resume Ranker and Advanced JD Search.",
            "Back to upload button and pagination.",
          ],
        },
      ],
    },
    {
      date: "12 May 2025",
      version: "16.03",
      logs: [
        {
          description: "Bug Fixes & Improvements",
          subPoints: [
            "AICV page URL and redirection fixes.",
            "Auto scroll to results table.",
            "Retry logic for file uploads.",
          ],
        },
      ],
    },
    {
      date: "09 May 2025",
      version: "16.02",
      logs: [
        {
          description: "Job Hopper Badge",
          subPoints: [
            "Added job hopper badge for recruiter and public page.",
            "Updated jobber image.",
          ],
        },
      ],
    },
    {
      date: "07 May 2025",
      version: "16.01",
      logs: [
        {
          description: "JD Ranker Integration",
          subPoints: [
            "JD Resume Ranker with Advanced Data Search combined page.",
            "Removed contact button on resume history and advanced data search.",
          ],
        },
      ],
    },
    {
      date: "06 May 2025",
      version: "16.00",
      logs: [
        {
          description: "Advanced Data Search",
          subPoints: [
            "Action button with functionality.",
            "Process button enable/disable based on errors and file selection.",
          ],
        },
      ],
    },
    {
      date: "05 May 2025",
      version: "15.05",
      logs: [
        {
          description: "Validation & UI",
          subPoints: [
            "Validation for more than 4.5 MB zip files.",
            "Process button disable/enable according to validation.",
            "Loader for advanced data search.",
          ],
        },
      ],
    },
    {
      date: "02 May 2025",
      version: "15.04",
      logs: [
        {
          description: "Advanced Search Fixes",
          subPoints: [
            "TablePagination page limit fix.",
            "Experience conversion from years to months in payload.",
            "Autocomplete dropdown design changes.",
          ],
        },
      ],
    },
    {
      date: "01 May 2025",
      version: "15.03",
      logs: [
        {
          description: "Advanced Search",
          subPoints: [
            "Dynamic fields and operators through API.",
            "Table with search data results.",
            "Resume history search based on status.",
          ],
        },
      ],
    },
    {
      date: "30 April 2025",
      version: "15.02",
      logs: [
        {
          description: "Resume Ranker APIs",
          subPoints: [
            "Resume Ranker APIs and design changes.",
            "Advanced search data filter with UI.",
            "Route created for advanced search.",
          ],
        },
      ],
    },
    {
      date: "28 April 2025",
      version: "15.01",
      logs: [
        {
          description: "Resume Ranker Component",
          subPoints: [
            "Added Resume Ranker component with routing.",
            "Mobile view support for ResumeRanker.",
            "Route for AICV page.",
          ],
        },
      ],
    },
    {
      date: "25 April 2025",
      version: "15.00",
      logs: [
        {
          description: "AICV & Recruiter History",
          subPoints: [
            "AICV homepage content for SEO with title and keywords.",
            "Search on status (Peer Reviewed, Self Attested, Resume Based).",
            "TablePagination scrolling fix.",
          ],
        },
      ],
    },
    {
      date: "24 April 2025",
      version: "14.05",
      logs: [
        {
          description: "Recruiter Features",
          subPoints: [
            "25 file limit for viewAsRecruiter users.",
            "Search on email and mobile in history table.",
            "Click-to-call and click-to-email functionality.",
            "Three badge display on carousel based on status.",
          ],
        },
      ],
    },
    {
      date: "23 April 2025",
      version: "14.04",
      logs: [
        {
          description: "Pagination & Recruiter View",
          subPoints: [
            "API-based page-wise data loading with cache.",
            "Email and mobile columns for viewAsRecruiter.",
            "viewAsRecruiter checkbox on login page.",
          ],
        },
        {
          description: "Interview Questions design improvements.",
        },
        {
          description: "Skill search and user form enhancements.",
        },
        {
          description: "Result Page New Sections",
          subPoints: [
            "Added Work Experience component.",
            "Added Projects and Achievements sections.",
            "Added Educational Qualification section.",
            "Added Languages section.",
            "Added Hobbies section.",
            "Added Work Activities section.",
            "Added Current Company & Written Company display.",
          ],
        },
        {
          description: "Resume Ranker Feature",
          subPoints: [
            "New algorithm for JD-Resume sorting.",
            "Meaningful messages while processing resumes.",
          ],
        },
        {
          description: "SEO Improvements",
          subPoints: [
            "Added sitemap file.",
          ],
        },
      ],
    },
    {
      date: "22 April 2025",
      version: "14.03",
      logs: [
        {
          description: "viewAsRecruiter Database",
          subPoints: [
            "viewAsRecruiter flag from database controls feature visibility.",
          ],
        },
      ],
    },
    {
      date: "21 April 2025",
      version: "14.02",
      logs: [
        {
          description: "Public Profile Updates",
          subPoints: [
            "Date and time display in public profile, TechWorth, and result pages.",
            "PDF text in userform data for result API.",
            "Removed TechWorth redirection from recruiter history table.",
          ],
        },
      ],
    },
    {
      date: "18 April 2025",
      version: "14.01",
      logs: [
        {
          description: "Recruiter Resume History",
          subPoints: [
            "Added RecruiterResumeHistory table page.",
            "Separate candidate profile page view.",
            "Recruiter checkbox login functionality.",
          ],
        },
      ],
    },
    {
      date: "16 April 2025",
      version: "14.00",
      logs: [
        {
          description: "History Table Features",
          subPoints: [
            "Contact button on history table with email API.",
            "Search by Years of Experience.",
            "Amplify deployment configuration.",
          ],
        },
        {
          description: "SEO & Verification",
          subPoints: [
            "Google site verification and sitemap.",
            "No data found card with upload button.",
          ],
        },
      ],
    },
    {
      date: "15 April 2025",
      version: "13.05",
      logs: [
        {
          description: "Theme Persistence",
          subPoints: [
            "User selected theme saved and restored on return.",
            "Theme on profile page with header.",
            "Dynamic title for different hostnames.",
          ],
        },
      ],
    },
    {
      date: "12 April 2025",
      version: "13.04",
      logs: [
        {
          description: "Theme Customization",
          subPoints: [
            "User theme selection saved to database.",
            "Fixed theme dropdown slide effect.",
            "Typography theme changes.",
          ],
        },
      ],
    },
    {
      date: "11 April 2025",
      version: "13.03",
      logs: [
        {
          description: "Resume History Columns",
          subPoints: [
            "Added Years of Experience, Location, Last Company, Last Role columns.",
            "Search by new fields.",
            "Three badge status display.",
          ],
        },
        {
          description: "Theme Features",
          subPoints: ["Theme color and font changes based on user selection."],
        },
      ],
    },
    {
      date: "10 April 2025",
      version: "13.02",
      logs: [
        {
          description: "New Homepage Design",
          subPoints: [
            "SEO title, keywords, and description.",
            "Footer role SEO keywords.",
            "View template demo button.",
          ],
        },
        {
          description: "Status & Profile Features",
          subPoints: [
            "Status column with badge updates.",
            "Public profile remove dialog.",
            "Months to years conversion display.",
          ],
        },
      ],
    },
    {
      date: "08 April 2025",
      version: "13.01",
      logs: [
        {
          description: "Resume History",
          subPoints: [
            "Self attested column in history table.",
            "Search bar to filter rows within paginated data.",
          ],
        },
      ],
    },
    {
      date: "04 April 2025",
      version: "13.00",
      logs: [
        {
          description: "Resume History Feature",
          subPoints: [
            "Resume history with old uploaded resumes in table format.",
            "Pagination with view resume and public profile buttons.",
          ],
        },
        {
          description: "Theme Redux",
          subPoints: ["Created Redux for theme with color dropdown on header."],
        },
      ],
    },
    {
      date: "03 April 2025",
      version: "12.05",
      logs: [
        {
          description: "User Redirects",
          subPoints: [
            "Redirect user to targeted path after login.",
            "Login page image and profile new tab open.",
            "Profile update on new resume upload.",
          ],
        },
        {
          description: "UI Updates",
          subPoints: [
            "Professional background changes and skills validity.",
            "First info page new design.",
          ],
        },
      ],
    },
    {
      date: "31 March 2025",
      version: "12.04",
      logs: [
        {
          description: "Carousel Design",
          subPoints: [
            "All slides new design with public page design changes.",
            "Added profile route for profile page.",
          ],
        },
      ],
    },
    {
      date: "29 March 2025",
      version: "12.03",
      logs: [
        {
          description: "Resume Upload Enhancements",
          subPoints: [
            "Added warning when user uploads old resume.",
          ],
        },
        {
          description: "Salary Calculation Updates",
          subPoints: [
            "Added salary range for higher and lower values.",
          ],
        },
        {
          description: "Job Search Integration",
          subPoints: [
            "Added job search redirection feature.",
          ],
        },
      ],
    },
    {
      date: "28 March 2025",
      version: "12.02",
      logs: [
        {
          description: "Page Redesigns",
          subPoints: [
            "Homepage, Login page, and ResumeUpload page new design.",
            "Salary range changes.",
          ],
        },
      ],
    },
    {
      date: "26 March 2025",
      version: "12.01",
      logs: [
        {
          description: "TechWorth Integration",
          subPoints: [
            "Self attested flag for TechWorth app redirect.",
            "Made condition required for form submission.",
          ],
        },
        {
          description: "Interview Questions by Category",
          subPoints: [
            "Added interview questions categorized by skill type.",
            "Animation and design updates for interview questions.",
          ],
        },
      ],
    },
    {
      date: "25 March 2025",
      version: "12.00",
      logs: [
        {
          description: "Contact Us & Analytics",
          subPoints: [
            "Added Contact Us page with footer changes.",
            "Changed gtag to ReactGA for analytics.",
            "Loading skeleton and animation improvements.",
          ],
        },
        {
          description: "Bug Fixes",
          subPoints: [
            "Skills color based on tech/management categories.",
            "Login loader issue fix for fetchCountry.",
          ],
        },
      ],
    },
    {
      date: "24 March 2025",
      version: "11.05",
      logs: [
        {
          description: "Branding & Analytics",
          subPoints: [
            "Added favicon and animation string loading component.",
            "Google Analytics events for resume upload and home page.",
          ],
        },
      ],
    },
    {
      date: "21 March 2025",
      version: "11.04",
      logs: [
        {
          description: "API & Location",
          subPoints: [
            "Fixed 504 API timeout issue.",
            "Only accept PDF files for upload.",
            "Nearest location and role suggestions.",
          ],
        },
      ],
    },
    {
      date: "20 March 2025",
      version: "11.03",
      logs: [
        {
          description: "Validation & UX",
          subPoints: [
            "Warning for resume older than 6 months.",
            "Retry logic for API calls.",
            "Animation string and text changes.",
          ],
        },
      ],
    },
    {
      date: "19 March 2025",
      version: "11.02",
      logs: [
        {
          description: "UI Improvements",
          subPoints: [
            "Avatar skills text and design changes.",
            "Removed unnecessary and commented code.",
          ],
        },
      ],
    },
    {
      date: "17 March 2025",
      version: "11.01",
      logs: [
        {
          description: "Error Pages",
          subPoints: ["Error page for public profile with 404 handling."],
        },
      ],
    },
    {
      date: "13 March 2025",
      version: "11.00",
      logs: [
        {
          description: "Security & Bug Fixes",
          subPoints: [
            "Removed static encryption key and secret values.",
            "Public page design fix.",
            "Default value for month validity harness.",
          ],
        },
      ],
    },
    {
      date: "12 March 2025",
      version: "10.05",
      logs: [
        {
          description: "Data Encryption",
          subPoints: [
            "Data encryption/decryption implementation.",
            "Updated public page design.",
            "Name outside of slides design change.",
          ],
        },
      ],
    },
    {
      date: "11 March 2025",
      version: "10.04",
      logs: [
        {
          description: "Error Handling & Design",
          subPoints: [
            "Name section added to slide with resume-based batch.",
            "Detailed error handling.",
            "Selected file shown at every step.",
            "Back button for form questions.",
          ],
        },
      ],
    },
    {
      date: "08 March 2025",
      version: "10.03",
      logs: [
        {
          description: "Validation Features",
          subPoints: [
            "Public page loader.",
            "Experience validation.",
            "File size limit validation.",
            "6-month-old resume validation.",
          ],
        },
      ],
    },
    {
      date: "07 March 2025",
      version: "10.02",
      logs: [
        {
          description: "Animation Updates",
          subPoints: ["Animation text changes and other improvements."],
        },
      ],
    },
    {
      date: "06 March 2025",
      version: "10.01",
      logs: [
        {
          description: "Carousel Design",
          subPoints: [
            "Updated form data to carousel.",
            "Fixed height and scroll for slides.",
          ],
        },
      ],
    },
    {
      date: "05 March 2025",
      version: "10.00",
      logs: [
        {
          description: "Resume Form",
          subPoints: [
            "Added role, location, and experience questions.",
            "Google and LinkedIn login fixes.",
          ],
        },
      ],
    },
    {
      date: "03 March 2025",
      version: "9.05",
      logs: [
        {
          description: "Public Page & Slides",
          subPoints: [
            "Public page pending changes.",
            "Added scroller for long slides.",
          ],
        },
      ],
    },
    {
      date: "28 February 2025",
      version: "9.04",
      logs: [
        {
          description: "Salary Display",
          subPoints: [
            "Salary and company TechValue on last slide.",
            "Design changes.",
          ],
        },
      ],
    },
    {
      date: "26 February 2025",
      version: "9.03",
      logs: [
        {
          description: "Content Updates",
          subPoints: [
            "Added projects and achievements.",
            "Consider information design changes.",
            "Updated homepage.",
          ],
        },
      ],
    },
    {
      date: "25 February 2025",
      version: "9.02",
      logs: [
        {
          description: "Sharing & UI",
          subPoints: [
            "Solved public page share issue.",
            "Resume view button.",
            "Consideration field slide.",
          ],
        },
      ],
    },
    {
      date: "21 February 2025",
      version: "9.01",
      logs: [
        {
          description: "Skills & Validation",
          subPoints: [
            "Skills validity color indicators.",
            "Form missing field handling.",
            "Experience issue resolved.",
          ],
        },
      ],
    },
    {
      date: "18 February 2025",
      version: "9.00",
      logs: [
        {
          description: "Profile Links",
          subPoints: [
            "LinkedIn and GitHub URL support.",
            "Professional background design change.",
            "Potential salary display.",
          ],
        },
        {
          description: "Public Carousel",
          subPoints: [
            "Resume public carousel.",
            "TechWorth banner.",
            "SEO keywords.",
          ],
        },
      ],
    },
    {
      date: "12 February 2025",
      version: "8.05",
      logs: [
        {
          description: "TechWorth",
          subPoints: [
            "TechWorth redirection link.",
            "Note text for parsing.",
          ],
        },
      ],
    },
    {
      date: "11 February 2025",
      version: "8.04",
      logs: [
        {
          description: "Work Experience",
          subPoints: [
            "Added work experience and education.",
            "Package updates.",
          ],
        },
      ],
    },
    {
      date: "07 February 2025",
      version: "8.03",
      logs: [
        {
          description: "Experience & TechValue",
          subPoints: ["Experience and TechValue changes."],
        },
      ],
    },
    {
      date: "06 February 2025",
      version: "8.02",
      logs: [
        {
          description: "Login & Features",
          subPoints: ["Login email functionality.", "TechValue integration."],
        },
      ],
    },
    {
      date: "03 February 2025",
      version: "8.01",
      logs: [
        {
          description: "Carousel Settings",
          subPoints: ["Slide auto play changes."],
        },
      ],
    },
    {
      date: "31 January 2025",
      version: "8.00",
      logs: [
        {
          description: "Initial Commit",
          subPoints: ["Resume Carousel SPA initial version."],
        },
      ],
    },
    {
      date: "13 January 2025",
      version: "7.05",
      logs: [
        {
          description: "Google Analytics Integration",
          subPoints: [
            "Added GA events for Check Relevance and Ask AI buttons.",
          ],
        },
        {
          description: "Error handling improvements for Ask AI and Check Relevance APIs.",
        },
      ],
    },
    {
      date: "08 January 2025",
      version: "7.04",
      logs: [
        {
          description: "Resume Parsing Feature",
          subPoints: [
            "Added resume parse feature for automatic data extraction.",
            "Get TechValue from resume.",
          ],
        },
        {
          description: "Result Page Carousel",
          subPoints: [
            "Added PublicPage Carousel for ResultPage.",
          ],
        },
      ],
    },
    {
      date: "23 December 2024",
      version: "7.03",
      logs: [
        {
          description: "Get Insights Enhancements",
          subPoints: [
            "Added skeleton loaders for Get Insights section.",
            "Optimized interview question API calls.",
            "Updated industry usage color theme.",
          ],
        },
        {
          description: "TechWorth Banner",
          subPoints: [
            "Added banner for TechWorth with read more option.",
          ],
        },
        {
          description: "Interview Questions & Career Disruption",
          subPoints: [
            "Added interview questions based on skills.",
            "Added career disruption insights.",
            "Tour guide for interview questions section.",
          ],
        },
        {
          description: "UI/UX Updates",
          subPoints: [
            "Loading and error animations.",
            "Button animations for better interaction.",
            "Subskills display for top 5 skills.",
          ],
        },
      ],
    },
    {
      date: "20 December 2024",
      version: "7.02",
      logs: [
        {
          description: "Result Page Redesign",
          subPoints: [
            "Complete redesign of ResultPage and PreviousResultPage according to Figma.",
            "Added professional background section.",
            "Section reorder, hide and show through database configuration.",
          ],
        },
        {
          description: "Stepper Form Enhancements",
          subPoints: [
            "Added motion animations between steps.",
            "Added loading animation component.",
            "Made steps button fixed for mobile view.",
          ],
        },
        {
          description: "References Feature",
          subPoints: [
            "Added dialog table for references endorsed.",
          ],
        },
        {
          description: "Pricing Updates",
          subPoints: [
            "Updated pricing based on months.",
          ],
        },
      ],
    },
    {
      date: "23 November 2024",
      version: "7.01",
      logs: [
        {
          description: "Subskill Enhancements",
          subPoints: [
            "Show unselected subskills.",
            "Fallback to parent logos.",
          ],
        },
        {
          description: "Updated algorithms for expertise endorsement.",
        },
      ],
    },
    {
      date: "11 October 2024",
      version: "7.00",
      logs: [
        {
          description: "Expertise Endorsement",
          subPoints: ["Added TechWorth with progress UI."],
        },
        {
          description: "Subskill Features",
          subPoints: [
            "Introduced multi-select options.",
            "Added color-coded learning ease.",
          ],
        },
      ],
    },
    {
      date: "09 September 2024",
      version: "6.05",
      logs: [
        {
          description: "UI/UX Updates",
          subPoints: [
            "Added Incognito Info Page.",
            "Improved Result Pages design.",
            "Refined Toggle Switch functionality.",
          ],
        },
      ],
    },
    {
      date: "28 August 2024",
      version: "6.04",
      logs: [
        {
          description: "Result page : Added 'Help Friends' button.",
        },
        {
          description:
            "Enhanced Skill Icons for visibility and dynamic resizing.",
        },
      ],
    },
    {
      date: "05 July 2024",
      version: "6.03",
      logs: [
        {
          description: "Launched Incognito Mode for privacy.",
        },
        {
          description: "Added Synonymous Search.",
        },
      ],
    },
    {
      date: "01 June 2024",
      version: "6.02",
      logs: [
        {
          description: "UX Updates",
          subPoints: [
            "Redesigned home page with updated terms and conditions, adding Google and LinkedIn buttons.",
            "Redesigned TechRelevance result page with improved colour schemes.",
          ],
        },
      ],
    },
    {
      date: "01 May 2024",
      version: "6.01",
      logs: [
        {
          description: "Feature Enhancements",
          subPoints: [
            "Improved the tech relevance algorithm and skill handling with updated prompts and conditions.",
            "Added new features including UAE location support.",
            "Enhanced user login functionality.",
            "New coin-based reward system on the 'My Friends' page.",
          ],
        },
        {
          description: "UX Updates",
          subPoints: [
            "Redesigned home, result, and referral pages with new elements like country flags and user drop-downs.",
            "Added app tours for easier user understanding on home and result pages.",
          ],
        },
      ],
    },
    {
      date: "01 April 2024",
      version: "6.00",
      logs: [
        {
          description: "UI/UX Updates",
          subPoints: [
            "Redesigned login, home, and result pages with new animations and responsive design.",
            "Enhanced referral tracking with pagination and mobile-friendly views for referral tables.",
          ],
        },
      ],
    },
    {
      date: "01 March 2024",
      version: "5.05",
      logs: [
        {
          description: "Feature Enhancements",
          subPoints: [
            "Added GenAI extensions for skill estimation and suggestions using ChatGPT.",
            "Implemented TrueEdge popup dialog and responsive design improvements.",
          ],
        },
        {
          description: "UX Updates",
          subPoints: [
            "Redesigned login, home, and result pages.",
            "Improved referral tracking with mobile-friendly tables and pagination.",
          ],
        },
      ],
    },
    {
      date: "01 February 2024",
      version: "5.04",
      logs: [
        {
          description: "Feature Enhancements",
          subPoints: [
            "Added LinkedIn authentication and job search integration.",
          ],
        },
        {
          description: "Backend Updates",
          subPoints: [
            "Enhanced algorithms for salary calculations and contract-specific logic.",
          ],
        },
      ],
    },
    {
      date: "01 January 2024",
      version: "5.03",
      logs: [
        {
          description: "Feature Enhancements",
          subPoints: [
            "Improved suggested skills and salary feedback API.",
            "Integrated ChatGPT for skill suggestions and interview questions.",
          ],
        },
        {
          description: "Backend Updates",
          subPoints: [
            "Enhanced the algorithm with country-specific compensation factors.",
          ],
        },
        {
          description: "UI/UX Improvements",
          subPoints: [
            "Redesigned the homepage and result page with fresh graphics.",
            "Added testimonials, user discounts, and cover letter functionality.",
          ],
        },
      ],
    },
    {
      date: "01 December 2023",
      version: "5.02",
      logs: [
        {
          description: "Referral and User Logic Enhancements",
          subPoints: [
            "Added unique IDs for referral URLs and improved social media referral handling.",
          ],
        },
        {
          description: "Backend and Algorithm Updates",
          subPoints: [
            "Enhanced TechValue calculations with updated location data.",
            "Integrated maxSalary into our algorithm for better salary predictions.",
          ],
        },
        {
          description: "UX Improvements",
          subPoints: [
            "Revamped Result and Referral pages with fresh designs and graphics.",
            "Added a maintenance dialog for users during updates.",
          ],
        },
        {
          description: "New Features",
          subPoints: [
            "Added OTP to email subject lines for easy viewing without opening the email.",
            "Incorporated new AI tools, certificates, and skills into the app.",
            "More locations added for AUS and NZ.",
          ],
        },
      ],
    },
    {
      date: "01 November 2023",
      version: "5.01",
      logs: [
        {
          description: "Result Management",
          subPoints: [
            "Last result can be viewed on login. Enhanced API for faster responses.",
          ],
        },
        {
          description: "Functional Upgrades",
          subPoints: [
            "Multiple business domains can be added as expertise.",
            "Enhanced Result Page for better clarity.",
            "Google-based one-click login enabled.",
            "Multiple locations added.",
          ],
        },
        {
          description: "Notification Enhancements",
          subPoints: [
            "Updated endorse email links and result emails to include salary data.",
          ],
        },
        {
          description: "New Features and API Development",
          subPoints: [
            "TrueEdge launched with ChatGPT API for cover letters.",
            "Added multi-country currency support for salary calculation.",
            "Enhanced domain acceptance for .edu and similar domains.",
          ],
        },
      ],
    },
    {
      date: "16 October 2023",
      version: "5.00",
      logs: [
        {
          description:
            "AI Boost - chatgpt Interface for Career Counseling Suggestions to increase salary",
        },
      ],
    },
    {
      date: "19 September 2023",
      version: "4.00",
      logs: [
        {
          description: '"Endorse your Expertise" feature with impact on salary',
        },
      ],
    },
    {
      date: "05 September 2023",
      version: "3.50",
      logs: [
        {
          description: "Lite Version TechRelevance Released",
        },
      ],
    },
    {
      date: "15 August 2023",
      version: "3.00",
      logs: [
        {
          description:
            "Peer Reviewed Expertise added, New Certificate format added",
        },
      ],
    },
    {
      date: "15 July 2023",
      version: "2.50",
      logs: [
        {
          description: "Role Seniority and Team Size weightages added",
        },
      ],
    },
    {
      date: "15 June 2023",
      version: "2.00",
      logs: [
        {
          description: "Secondary skill weightages added",
        },
      ],
    },
    {
      date: "01 June 2023",
      version: "1.50",
      logs: [
        {
          description:
            "500 new skills and certifications added - Suggestions from users",
        },
      ],
    },
    {
      date: "15 May 2023",
      version: "1.01",
      logs: [
        {
          description: "References made mandatory",
        },
      ],
    },
    {
      date: "01 May 2023",
      version: "1.00",
      logs: [
        {
          description: "Original Version",
        },
      ],
    },
  ];

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Header />
        </Grid>
        <Grid
          item
          xs={12}
          mx={{ xs: 0, sm: 0, md: 3, lg: 18 }}
          px={{ xs: 0 }}
          mt={{ xs: 12, md: 14, lg: 14 }}
          mb={{ xs: 2 }}
        >
          <Typography
            sx={{
              fontSize: { xs: "22px", sm: "28px", md: "44px" },
              fontWeight: 600,
              color: (theme) => theme.palette.primary.main,
              textAlign: "center",
            }}
            mb={{ xs: "20px", md: "69px", lg: "120px" }}
          >
            TrueSelfy TechWorth Algorithm Evolution
          </Typography>

          <Timeline
            sx={{
              mt: { xs: "60px", md: "120px", lg: "120px" },
              "& .MuiTimelineItem-root": {
                flexDirection: { xs: "column", md: "row" },
              },
              [`& .MuiTimelineOppositeContent-root`]: {
                flex: 0.3,
              },
              "& .MuiTimelineContent-root": {
                display: "block",
                marginTop: { xs: "0px", md: "-60px" },
                marginBottom: { xs: "40px", md: "120px" },
              },
              "& .MuiTimelineDot-root": {
                background: "transparent",
                width: "50px",
                height: "50px",
                border: "0px",
                padding: "0px",
                boxShadow: "none",
                display: "block",
                margin: "0px auto",
              },
              "& .MuiTimelineSeparator-root": {
                display: { xs: "none", md: "inline-flex" },
                flex: { xs: 0, md: 0.1 },
              },
              "& .MuiTimelineConnector-root": {
                background: (theme) => theme.palette.primary.main,
                width: "5px",
              },
            }}
          >
            {versionData &&
              versionData.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineOppositeContent>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 500,
                        color: "#232323",
                      }}
                    >
                      {item.date}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot>
                      <img
                        width="50px"
                        height="50px"
                        src="/assets/images/timeline-dot.svg"
                        alt="timeline-dot"
                        loading="lazy"
                      />
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box
                      sx={{
                        maxWidth: "1000px",
                        width: "100%",
                        padding: { xs: "20px", md: "33px" },
                        borderRadius: "10px",
                        background: "#F5F5F5",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "28px",
                          fontWeight: 600,
                          color: (theme) => theme.palette.primary.main,
                          marginBottom: { xs: "10px", md: "21px" },
                        }}
                      >
                        v{item.version}
                      </Typography>

                      <List
                        sx={{
                          ml: { xs: "15px", md: "50px" },
                          "& .MuiListItem-root": {
                            //padding: { xs: "6px", md: "12px" },
                            alignItems: "baseline",
                            padding: "0px !important",
                          },
                        }}
                      >
                        {item.logs.map((log, logIndex) => (
                          <React.Fragment key={logIndex}>
                            <ListItem>
                              <ListItemIcon
                                sx={{ minWidth: "unset", mr: "14px" }}
                              >
                                <Circle
                                  color="success"
                                  sx={{ fontSize: "14px" }}
                                />
                              </ListItemIcon>
                              <ListItemText>
                                <Typography
                                  sx={{ fontSize: { xs: "18px", md: "24px" }, color: "#232323" }}
                                >
                                  {log.description}
                                </Typography>
                              </ListItemText>
                            </ListItem>

                            {log.apps && log.apps.length > 0 && (
                              <Box sx={{ ml: { xs: "14px", md: "28px" }, mt: "6px", mb: "4px", display: "flex", flexWrap: "wrap", gap: { xs: "6px", md: "8px" } }}>
                                {log.apps.map((app, appIdx) => (
                                  <Chip
                                    key={appIdx}
                                    label={app.name}
                                    component={Link}
                                    href={app.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    clickable
                                    size="small"
                                    icon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                                    sx={{
                                      fontSize: { xs: "11px", md: "13px" },
                                      fontWeight: 500,
                                      bgcolor: "#e8f5e9",
                                      color: "#2e7d32",
                                      border: "1px solid #a5d6a7",
                                      "&:hover": { bgcolor: "#c8e6c9" },
                                      textDecoration: "none",
                                      height: { xs: "26px", md: "32px" },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}

                            {log.hasScreenshot && (log.screenshots?.length > 0 || log.screenshotUrl) && (
                              <Box sx={{ ml: { xs: "14px", md: "28px" }, mt: "8px", mb: "8px", display: "flex", flexWrap: "wrap", gap: { xs: "8px", md: "12px" } }}>
                                {(log.screenshots || (log.screenshotUrl ? [{ url: log.screenshotUrl, label: log.screenshotLabel }] : [])).map((ss, ssIdx) => (
                                  ss.url && (
                                    <Box
                                      key={ssIdx}
                                      sx={{ cursor: "pointer", display: "inline-block", WebkitTapHighlightColor: "transparent" }}
                                      onClick={() => setLightbox({ open: true, src: ss.url, label: ss.label || log.description })}
                                    >
                                      <Box
                                        sx={{
                                          width: { xs: "140px", sm: "160px", md: "200px" },
                                          height: { xs: "95px", sm: "108px", md: "130px" },
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                          border: "2px solid #e0e0e0",
                                          position: "relative",
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            border: "2px solid #4caf50",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            transform: "translateY(-2px)",
                                          },
                                          "&:hover > .MuiBox-root": { opacity: 1 },
                                        }}
                                      >
                                        <img
                                          src={ss.url}
                                          alt={ss.label || log.description}
                                          loading="lazy"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "top left",
                                          }}
                                        />
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            inset: 0,
                                            bgcolor: "rgba(0,0,0,0.4)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            opacity: 0,
                                            transition: "opacity 0.2s ease",
                                          }}
                                        >
                                          <PhotoCameraIcon sx={{ color: "#fff", fontSize: "28px" }} />
                                        </Box>
                                      </Box>
                                      <Typography sx={{
                                        fontSize: { xs: "10px", md: "11px" },
                                        color: "#757575",
                                        mt: "4px",
                                        maxWidth: { xs: "140px", sm: "160px", md: "200px" },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}>
                                        {ss.label || "Click to view"}
                                      </Typography>
                                    </Box>
                                  )
                                ))}
                              </Box>
                            )}

                            {log.subPoints && (
                              <List sx={{ ml: 4 }}>
                                {log.subPoints.map((subPoint, subIndex) => (
                                  <ListItem key={subIndex}>
                                    <ListItemIcon
                                      sx={{ minWidth: "unset", mr: "14px" }}
                                    >
                                      <Circle
                                        color="success"
                                        sx={{ fontSize: "10px" }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText>
                                      <Typography
                                        sx={{
                                          fontSize: { xs: "15px", md: "20px" },
                                          color: "#232323",
                                        }}
                                      >
                                        {subPoint}
                                      </Typography>
                                    </ListItemText>
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}
          </Timeline>
        </Grid>
        <Grid item xs={12}>
          <FooterComponent />
        </Grid>
      </Grid>

      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, src: "", label: "" })}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            boxShadow: isMobile ? "none" : "0 8px 32px rgba(0,0,0,0.5)",
            overflow: "hidden",
            maxHeight: isMobile ? "100vh" : "95vh",
            m: isMobile ? 0 : { md: "32px" },
            borderRadius: isMobile ? 0 : "12px",
          },
        }}
        BackdropProps={{
          sx: { bgcolor: "rgba(0,0,0,0.92)" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            height: isMobile ? "100vh" : "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: "12px", md: "20px" },
              py: "10px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: "13px", md: "14px" },
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mr: "8px",
              }}
            >
              {lightbox.label || "Screenshot"}
            </Typography>
            <IconButton
              onClick={() => setLightbox({ open: false, src: "", label: "" })}
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                width: 34,
                height: 34,
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <CloseIcon sx={{ fontSize: "20px", color: "#fff" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: "8px", md: "20px" },
              overflow: "auto",
            }}
          >
            <img
              src={lightbox.src}
              alt={lightbox.label}
              style={{
                maxWidth: "100%",
                maxHeight: isMobile ? "calc(100vh - 60px)" : "80vh",
                objectFit: "contain",
                borderRadius: "6px",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export default ChangeLog;
